import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Box,
  Grid,
  TextField,
  Typography,
  debounce,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { Fragment, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import CommonTextField from "../../../../../../common/components/functional/commonTextField/CommonTextField";
import i18n from "../../../../../../i18n";
import { CustomerOrganization } from "../../../../../customer/customerAPI";
import { fetchCustomerOrgAutocomplete } from "../../../../../customer/customerOrganizationsSlice";
import { selectAssignmentOptionsByEmploymentId } from "../../../AssignmentAdditionalInfo/AssignmentsSlice";
import { AccumulationField } from "../../types";

type Props = {
  fields: AccumulationField[];
  employmentId: string;
};

type Values = {
  formattedValue: string;
  value: string;
  floatValue: number | undefined;
};

const Input = ({ ...props }) => (
  <TextField
    {...props}
    {...(props?.inputType && { type: props.inputType })}
    autoComplete="off"
    sx={{ maxWidth: props?.maxWidth || "114px" }}
    size="small"
    inputProps={{
      ...props.inputProps,
    }}
  />
);

const AccumulationsEditFormFields = ({ fields, employmentId }: Props) => {
  const [custOrgSearchTerm, setCustOrgSearchTerm] = useState("");
  const [custOrgOptions, setCustOrgOptions] = useState<CustomerOrganization[]>(
    [],
  );

  const assignmentOptionsByEmploymentId = useAppSelector(
    selectAssignmentOptionsByEmploymentId(employmentId),
  );

  const { t } = useTranslation();
  const { control } = useFormContext();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (custOrgSearchTerm) {
      dispatch(fetchCustomerOrgAutocomplete({ term: custOrgSearchTerm })).then(
        (action) => {
          if (isFulfilled(action)) {
            setCustOrgOptions(action.payload);
          }
        },
      );
    }
  }, [custOrgSearchTerm, dispatch]);

  const onInputChange = debounce(
    (term: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setCustOrgSearchTerm(term);
      }
    },
    400,
  );

  const renderDynamicFields = (field: AccumulationField) => {
    const LabelComponent = (
      <Grid item xs={6} alignSelf="center" textAlign="end">
        <Box sx={{ wordWrap: "break-word" }}>
          <Typography
            variant="secondary"
            sx={{
              display: "block",
              "&::first-letter": { textTransform: "uppercase" },
            }}
          >
            {i18n.exists(`talentProfile:accumulationKeys:${field.label}`)
              ? t(`talentProfile:accumulationKeys:${field.label}`)
              : field.label}
          </Typography>
        </Box>
      </Grid>
    );

    switch (field.name) {
      case "assignmentId":
        return (
          <Fragment key={field.name}>
            {LabelComponent}
            <Grid item xs={6}>
              <Controller
                name={field.name}
                control={control}
                rules={{ required: true }}
                render={({ field }) => {
                  console.log("field", field);

                  return (
                    <Autocomplete
                      {...field}
                      options={assignmentOptionsByEmploymentId}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={field.value || null}
                      onChange={(_, newValue) => field.onChange(newValue)}
                      renderInput={(params: AutocompleteRenderInputParams) => (
                        <CommonTextField {...params} />
                      )}
                    />
                  );
                }}
              />
            </Grid>
          </Fragment>
        );
      case "lomapv":
        return (
          <Fragment key={field.name}>
            {LabelComponent}
            <Grid item xs={6}>
              <Controller
                name={field.name}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <NumericFormat
                    maxWidth="unset"
                    fullWidth
                    value={field.value}
                    onValueChange={(values: Values) =>
                      field.onChange(values.value)
                    }
                    customInput={Input}
                    decimalScale={0}
                    inputProps={{ maxLength: 3 }}
                  />
                )}
              />
            </Grid>
          </Fragment>
        );
      case "year":
        return (
          <Fragment key={field.name}>
            {LabelComponent}
            <Grid item xs={6}>
              <Controller
                name={field.name}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DesktopDatePicker
                    {...field}
                    value={field.value || null}
                    format="yyyy"
                    views={["year"]}
                    slots={{ textField: TextField }}
                    maxDate={new Date("2099-12-31")}
                    minDate={new Date("1900-01-01")}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Fragment>
        );
      case "valueDate":
        return (
          <Fragment key={field.name}>
            {LabelComponent}
            <Grid item xs={6}>
              <Controller
                name={field.name}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DesktopDatePicker
                    {...field}
                    value={field.value || null}
                    slots={{ textField: TextField }}
                    maxDate={new Date("2099-12-31")}
                    minDate={new Date("1900-01-01")}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Fragment>
        );
      case "month":
        return (
          <Fragment key={field.name}>
            {LabelComponent}
            <Grid item xs={6}>
              <Controller
                name={field.name}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DesktopDatePicker
                    {...field}
                    value={field.value || null}
                    format="MM"
                    views={["month"]}
                    slots={{ textField: TextField }}
                    maxDate={new Date("2099-12-31")}
                    minDate={new Date("1900-01-01")}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Fragment>
        );
      case "custOrgId":
        return (
          <Fragment key={field.name}>
            {LabelComponent}
            <Grid item xs={6}>
              <Controller
                name={field.name}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={custOrgOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={field.value?.id ? field.value : null}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    onInputChange={(_, newValue, reason) =>
                      onInputChange(newValue, reason)
                    }
                    renderInput={(params: AutocompleteRenderInputParams) => (
                      <CommonTextField {...params} />
                    )}
                  />
                )}
              />
            </Grid>
          </Fragment>
        );
      default:
        return (
          <Fragment key={field.name}>
            {LabelComponent}
            <Grid item xs={6}>
              <Controller
                name={field.name}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <NumericFormat
                    maxWidth="unset"
                    fullWidth
                    value={field.value}
                    onValueChange={(values: Values) =>
                      field.onChange(values.value)
                    }
                    valueIsNumericString
                    allowNegative
                    customInput={Input}
                    decimalSeparator=","
                    decimalScale={8}
                  />
                )}
              />
            </Grid>
          </Fragment>
        );
    }
  };

  return (
    <>
      <Grid item xs={6} alignSelf="center" textAlign="end">
        <Typography sx={{ wordWrap: "break-word" }} variant="secondary">
          {t("talentProfile:accumulationKeys:modificationDate")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name="modificationDate"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              format="dd.MM.yyyy"
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          )}
        />
      </Grid>
      {fields.map((field: AccumulationField) => renderDynamicFields(field))}
    </>
  );
};

export default AccumulationsEditFormFields;
